import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";
import { HandleUnsaveNavigationWithFun } from "../../../utilities/handleUnsaveNavigationWithFun";
import {
  createCorrectionService,
  getChangeQuestionnaireResponseService,
  getDataCorrectionDataOnLoadService,
  getDiaryEntryService,
  getDiaryPagesService,
  getPatientAttribuesService,
} from "../../../services/subjectService";
import {
  buildFormFields,
  buildPatientAttributeFields,
  getFieldType,
} from "../../Subject/AddUpdateSubject/DataCorrection/Helper/helper";
import {
  generateShortId,
  generateUUID,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import { findNode } from "../../Subject/DynamicForm/helper";
import SearchableSelect from "../../../components/SearchableSelect";
import { Box, Grid2, TextField } from "@mui/material";
import UncontrolledSearchSelect from "../../../components/UncontrolledSearchSelect";
import DataCorrectionGrid from "../../../components/DataCorrectionGrid/DataCorrectionGrid";
import DataCorrectionConfirmation from "../../Subject/AddUpdateSubject/DataCorrection/DataCorrectionConfirmation/DataCorrectionConfirmation";
import SubmitDataCorrection from "../../Subject/AddUpdateSubject/DataCorrection/SubmitDataCorrection/SubmitDataCorrection";
import Loader from "../../../components/Loader";
import DataCorrectionModal from "../../../components/DataCorrectionModal/DataCorrectionModal";
import { getPatientsBySiteService } from "../../../services/dataCorrectionsService";

// Constants
const CORRECTION_TYPES = {
  CHANGE_QUESTIONNAIRE_RESPONSES: "Change Questionnaire responses",
  CHANGE_QUESTIONNAIRE_INFO: "Change Questionnaire information",
  CHANGE_PATIENT_VISIT: "Change Subject Visit",
  CHANGE_SUBJECT_INFO: "Change Subject Information",
  MERGE_SUBJECTS: "Merge Subjects",
  REMOVE_SUBJECT: "Remove A Subject",
};

const useLocalStorage = () => {
  const access_token = localStorage.getItem("access_token");
  const roleId = localStorage.getItem("roleId");
  const cultureCode = localStorage.getItem("currentUserCultureCode");
  const email = localStorage.getItem("email");
  const singlePatientAlias = localStorage.getItem("singlePatientAlias");
  const globalDateFormat = localStorage.getItem("globalDateFormat");
  const studyName = localStorage.getItem("studyName");

  return {
    access_token,
    roleId,
    cultureCode,
    email,
    singlePatientAlias,
    globalDateFormat,
    studyName,
  };
};

const AddDataCorrections = () => {
  const id = useLocation()?.state?.id;
  const navigate = useNavigate();
  const storage = useLocalStorage();

  // State management
  const [load, setLoad] = useState(false);
  const [subjectName, setSubjectName] = useState("");
  const [siteName, setSiteName] = useState("");
  const [correctionData, setCorrectionData] = useState([]);
  const [sitesData, setSitesData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);
  const [ddlResponseData, setDdlReponseData] = useState([]);
  const [showSubmitPage, setShowSubmitPage] = useState(false);
  const [submitData, setSubmitData] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [password, setPassword] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [correctionPayload, setCorrectionPayload] = useState({
    createPayload: null,
    correctionId: "",
    correctionStatusId: "",
  });
  const [payload, setPayload] = useState(null);
  const [allJsons, setAllJsons] = useState([]);
  const [questionJson, setQuestionJson] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [correctionTableData, setCorrectionTableData] = useState([]);
  const [showMergeSubjectWarning, setMergeSubjectWarning] = useState(false);
  const [showRemoveSubject, setShowRemoveSubject] = useState(false);
  const [patientAttribute, setPatientAttribute] = useState(null);
  const [siteId, setSiteId] = useState(null);
  const [visitsData, setVisitsData] = useState([]);
  const [isPatientAttributeDisabled, setIsPatientAttributeDisabled] =
    useState(false);
  const [visitNumber, setVisitNumber] = useState("");

  // Navigation guards
  const handleUnsaveNavigation = useMemo(
    () => CreateHandleUnsaveNavigation(),
    [],
  );
  const handleUnSaveWithFun = useMemo(
    () => HandleUnsaveNavigationWithFun(),
    [],
  );

  // Form setup
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        typeOfCorrection: yup
          .string()
          .required("Type of Correction is required"),
        selectedSiteName: yup.string().required("Site is required"),
        selectedSubjectName: yup.string().required("Subject is required"),
        reason: yup.string().required("Reason for Correction is required"),
      }),
    [],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      reason: "",
      typeOfCorrection: "",
    },
  });

  const typeOfCorrection = watch("typeOfCorrection");
  const selectedSite = watch("selectedSiteName");
  const selectedSubject = watch("selectedSubjectName");

  const currentCorrectionType = useMemo(
    () => correctionData?.find((ele) => ele?.id === typeOfCorrection),
    [correctionData, typeOfCorrection],
  );

  // Data fetching functions
  const getOnLoadData = useCallback(async () => {
    if (!storage.roleId || !storage.cultureCode || !storage.access_token)
      return;

    try {
      setLoad(true);
      const res = await getDataCorrectionDataOnLoadService(
        storage.access_token,
        "",
        storage.roleId,
        storage.cultureCode,
      );

      if (
        res?.status === 200 &&
        res?.data?.message === "Online data extracted successfylly!"
      ) {
        const viewData = res?.data?.result?.correction;
        const formData = res?.data?.result?.viewBagData;
        const sitesData = res?.data.result?.viewBagData?.sites;

        setSubjectName(viewData?.patientNumber);
        setSiteId(viewData?.siteId);
        setPayload(viewData);
        setSiteName(viewData?.siteName);
        setSitesData(sitesData);
        setCorrectionData(formData?.correctionTypes);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      if (err?.status === 401) navigate("/unAuthorized");
    } finally {
      setLoad(false);
    }
  }, [storage, navigate]);

  const getSubjectData = useCallback(async () => {
    if (!selectedSite || !storage.access_token) return;

    try {
      setLoad(true);
      const res = await getPatientsBySiteService(
        storage.access_token,
        selectedSite,
      );
      if (
        res?.status == 200 &&
        res?.data?.message === "Extracted Data Successfully!"
      ) {
        setSubjectsData(res?.data?.result);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      if (err?.status === 401) navigate("/unAuthorized");
    } finally {
      setLoad(false);
    }
  }, [storage, navigate]);

  const getResponseForChangeQuestionnaireResponse = useCallback(async () => {
    if (
      !storage.access_token ||
      !storage.roleId ||
      !storage.cultureCode ||
      !payload
    )
      return;

    try {
      setLoad(true);
      const res = await getChangeQuestionnaireResponseService(
        storage.access_token,
        storage.roleId,
        storage.cultureCode,
        payload,
      );

      if (
        res?.status === 200 &&
        res?.data?.message === "executed successfully!"
      ) {
        setCorrectionPayload({
          correctionId: res?.data?.result?.correction?.id,
          createPayload: res?.data?.result?.correction,
          correctionStatusId: res?.data?.result?.correctionStatusId,
        });
      }
    } catch (err) {
      console.error("Error getting questionnaire response:", err);
      if (err.status === 401) navigate("/unAuthorized");
    } finally {
      setLoad(false);
    }
  }, [storage, payload, navigate]);

  const getDataForChangeDropdown = useCallback(
    async (displayName) => {
      if (
        !storage.access_token ||
        // !id ||
        !selectedSubject ||
        !storage.globalDateFormat ||
        !storage.cultureCode
      )
        return;

      const needsDropdown = [
        CORRECTION_TYPES.CHANGE_QUESTIONNAIRE_RESPONSES,
        CORRECTION_TYPES.CHANGE_QUESTIONNAIRE_INFO,
        CORRECTION_TYPES.CHANGE_PATIENT_VISIT,
      ].includes(displayName);

      if (!needsDropdown) return;

      try {
        setLoad(true);
        const res = await getDiaryPagesService(
          storage.access_token,
          selectedSubject,
          storage.globalDateFormat,
          storage.cultureCode,
        );

        if (
          res?.status === 200 &&
          res?.data?.message === "executed successfully!"
        ) {
          setDdlReponseData(res?.data?.result?.diaryEntries);
          setVisitsData(res?.data?.result?.visits);
        }
      } catch (err) {
        console.error("Error getting dropdown data:", err);
        if (err?.status === 401) navigate("/unAuthorized");
      } finally {
        setLoad(false);
      }
    },
    [selectedSubject, storage, navigate],
  );

  const getDiaryEntry = useCallback(
    async (correctionId, diaryEntryId) => {
      if (
        !storage.access_token ||
        !correctionId ||
        !diaryEntryId ||
        !storage.cultureCode ||
        !storage.globalDateFormat
      )
        return;

      try {
        setLoad(true);
        const res = await getDiaryEntryService(
          storage.access_token,
          correctionId,
          diaryEntryId,
          storage.cultureCode,
          storage.globalDateFormat,
        );

        if (
          res.status === 200 &&
          res?.data?.message === "executed successfully!"
        ) {
          const data = res?.data?.result?.diaryEntry;
          setAnswers(data?.answers);

          const newJsons =
            data?.questionAnswers?.map((item) =>
              JSON.parse(item?.question?.json),
            ) || [];
          setAllJsons(newJsons);

          const lastQuestion =
            data?.questionAnswers?.[data?.questionAnswers?.length - 1]?.question
              ?.json;
          setQuestionJson(lastQuestion ? JSON.parse(lastQuestion) : null);
        }
      } catch (err) {
        console.error("Error getting diary entry:", err);
      } finally {
        setLoad(false);
      }
    },
    [storage],
  );

  const getPatientAttributes = useCallback(
    async (correctionId) => {
      if (
        !storage.access_token ||
        !selectedSubject ||
        !correctionId ||
        !storage.cultureCode
      )
        return;

      try {
        setLoad(true);
        const res = await getPatientAttribuesService(
          storage.access_token,
          selectedSubject,
          correctionId,
          storage.cultureCode,
          null,
        );

        if (res?.status === 200 && res?.data?.result?.length > 0) {
          const data = res?.data?.result;
          setPatientAttribute(data?.map((item) => item.patientAttribute));
          setQuestionJson(JSON.parse(data?.[0]?.question?.json));
        } else {
          setPatientAttribute(null);
        }
      } catch (err) {
        console.error("Error getting patient attributes:", err);
      } finally {
        setLoad(false);
      }
    },
    [storage, id],
  );

  // Effects
  useEffect(() => {
    if (storage.roleId && storage.cultureCode && storage.access_token) {
      getOnLoadData();
    }
  }, []);

  useEffect(() => {
    if (selectedSite) {
      getSubjectData();
    }
  }, [selectedSite]);

  useEffect(() => {
    if (!typeOfCorrection || !correctionData?.length) return;

    const displayName = currentCorrectionType?.displayName;

    if (
      [
        CORRECTION_TYPES.CHANGE_QUESTIONNAIRE_RESPONSES,
        CORRECTION_TYPES.CHANGE_QUESTIONNAIRE_INFO,
        CORRECTION_TYPES.CHANGE_PATIENT_VISIT,
      ].includes(displayName)
    ) {
      getResponseForChangeQuestionnaireResponse();
      getDataForChangeDropdown(displayName);
      setMergeSubjectWarning(false);
      setShowRemoveSubject(false);
    } else if (displayName === CORRECTION_TYPES.CHANGE_SUBJECT_INFO) {
      getResponseForChangeQuestionnaireResponse();
    } else if (displayName === CORRECTION_TYPES.MERGE_SUBJECTS) {
      setSelectedQuestionnaire(null);
      setMergeSubjectWarning(true);
      setShowRemoveSubject(false);
    } else if (displayName === CORRECTION_TYPES.REMOVE_SUBJECT) {
      getResponseForChangeQuestionnaireResponse();
      setSelectedQuestionnaire(null);
      setMergeSubjectWarning(false);
      setShowRemoveSubject(true);
    }
  }, [
    typeOfCorrection,
    correctionData,
    currentCorrectionType,
    selectedSubject,
  ]);

  useEffect(() => {
    if (!typeOfCorrection || !currentCorrectionType) return;

    const displayName = currentCorrectionType?.displayName;
    const needsDiaryEntry =
      displayName !== CORRECTION_TYPES.CHANGE_SUBJECT_INFO;
    const hasCorrectionId = correctionPayload?.correctionId;

    if (needsDiaryEntry && selectedQuestionnaire && hasCorrectionId) {
      getDiaryEntry(correctionPayload.correctionId, selectedQuestionnaire);
    } else if (!needsDiaryEntry && hasCorrectionId) {
      setSelectedQuestionnaire(null);
      getPatientAttributes(correctionPayload.correctionId);
    }
  }, [
    selectedQuestionnaire,
    correctionPayload.correctionId,
    typeOfCorrection,
    currentCorrectionType,
    selectedSubject,
  ]);

  useEffect(() => {
    if (!typeOfCorrection || !currentCorrectionType) return;

    const displayName = currentCorrectionType?.displayName;
    const isPatientAttributeEmpty =
      !patientAttribute || patientAttribute?.length === 0;

    if (displayName === CORRECTION_TYPES.CHANGE_SUBJECT_INFO) {
      setIsPatientAttributeDisabled(isPatientAttributeEmpty);
    } else {
      setIsPatientAttributeDisabled(false);
    }
  }, [typeOfCorrection, currentCorrectionType, patientAttribute]);

  useEffect(() => {
    if (isAuthorized) {
      setShowConfirmation(true);
    }
  }, [isAuthorized]);

  // Build correction table data
  useEffect(() => {
    if (!typeOfCorrection || !currentCorrectionType) return;

    const displayName = currentCorrectionType?.displayName;

    if (displayName !== CORRECTION_TYPES.CHANGE_SUBJECT_INFO) {
      if (answers?.length > 0 && questionJson) {
        setCorrectionTableData(
          buildFormFields(
            answers,
            questionJson,
            allJsons,
            displayName,
            visitsData,
            visitNumber,
          ),
        );
      }
    } else {
      setCorrectionTableData(
        buildPatientAttributeFields(patientAttribute, questionJson),
      );
    }
  }, [
    answers,
    questionJson,
    typeOfCorrection,
    currentCorrectionType,
    allJsons,
    visitsData,
    visitNumber,
    patientAttribute,
  ]);

  // Form submission handlers
  const buildCorrectionPayload = useCallback(
    (data, temp) => {
      const displayName = currentCorrectionType?.displayName;

      return {
        ...correctionPayload.createPayload,
        correctionType: {
          id: typeOfCorrection,
          startedByUserId: storage.roleId,
          correctionStatusId: correctionPayload.correctionStatusId,
          displayTextAsColumnValue:
            correctionPayload.createPayload?.correctionType
              ?.displayTextAsColumnValue,
          displayName: currentCorrectionType?.displayName,
          displayDescription: currentCorrectionType?.displayDescription,
          translationKey: generateShortId(),
        },
        displayName: currentCorrectionType?.displayName,
        displayDescription: currentCorrectionType?.displayDescription,
        translationKey: generateShortId(),
        correctionTypeId: typeOfCorrection,
        reasonForCorrection: data.reason,
        patientId: selectedSubject,
        correctionApprovalData: temp,
        diaryEntry: ddlResponseData?.find(
          (ele) => ele?.id === selectedQuestionnaire,
        ),
        ...(selectedQuestionnaire && { diaryEntryId: selectedQuestionnaire }),
      };
    },
    [
      correctionPayload,
      typeOfCorrection,
      currentCorrectionType,
      storage.roleId,
      ddlResponseData,
      selectedQuestionnaire,
    ],
  );

  const buildCorrectionItems = useCallback(
    (data) => {
      const temp = [];
      const displayName = currentCorrectionType?.displayName;

      if (showRemoveSubject) {
        temp.push({
          oldDisplayValue: "Enrolled",
          newDisplayValue: "Removed",
          description: currentCorrectionType?.displayDescription,
        });
      } else if (displayName === CORRECTION_TYPES.CHANGE_SUBJECT_INFO) {
        patientAttribute?.forEach((item) => {
          if (item?.displayValue !== "") {
            const questionId = item?.patientAttributeConfigurationDetail?.id;
            let requestedField = "";
            let deleteField = false;

            Object.keys(data).forEach((ele) => {
              const [id, type] = ele.split("_");
              if (id == questionId) {
                if (type === "requested") requestedField = data[ele];
                if (type === "remove") deleteField = data[ele];
              }
            });

            temp.push({
              id: item?.id,
              questionId: item?.questionId,
              oldDisplayValue: item?.value,
              newDisplayValue: requestedField || "",
              removeItem: deleteField,
              description: currentCorrectionType?.displayDescription,
            });
          }
        });
      } else if (displayName === CORRECTION_TYPES.CHANGE_PATIENT_VISIT) {
        answers?.forEach((item) => {
          if (item?.displayAnswer !== "") {
            const questionId = item?.questionId;
            let requestedField = "";
            let deleteField = false;

            Object.keys(data).forEach((ele) => {
              const [id, type] = ele.split("_");
              if (id == questionId) {
                if (type === "requested") requestedField = data[ele];
                if (type === "remove") deleteField = data[ele];
              }
            });

            if (requestedField || deleteField) {
              temp.push({
                id: item?.id,
                questionId: item?.questionId,
                oldDisplayValue: item?.value,
                newDisplayValue: requestedField || "",
                removeItem: deleteField,
                description: currentCorrectionType?.displayDescription,
              });
            }
          }
        });
      } else if (displayName === CORRECTION_TYPES.CHANGE_QUESTIONNAIRE_INFO) {
        // Handle visit date picker
        const visitDateItem = answers?.find((item) => {
          if (!item?.displayAnswer) return false;
          const questionNode = findNode(questionJson, item?.questionId);
          const fieldType = getFieldType(questionNode, item);
          return fieldType === "visitdatepicker";
        });

        if (visitDateItem) {
          const questionId = visitDateItem?.questionId;
          const requestedField = data?.[`${questionId}_requested`] || "";
          const deleteField = data?.[`${questionId}_remove`] || false;

          if (requestedField || deleteField) {
            temp.push({
              id: generateUUID(),
              questionId: visitDateItem?.questionId,
              oldDisplayValue: visitDateItem?.value,
              newDisplayValue: requestedField,
              removeItem: deleteField,
              description: currentCorrectionType?.displayDescription,
            });
          }
        }

        // Handle visit name
        const visitId = data?.visit_name_requested;
        if (visitId) {
          temp.push({
            id: generateUUID(),
            questionId: generateUUID(),
            oldDisplayValue: visitNumber,
            newDisplayValue: visitId,
            removeItem: false,
            description: currentCorrectionType?.displayDescription,
          });
        }
      } else {
        answers?.forEach((item) => {
          if (item?.displayAnswer !== "") {
            const questionId = item?.questionId;
            let requestedField = "";
            let deleteField = false;

            Object.keys(data).forEach((ele) => {
              const [id, type] = ele.split("_");
              if (id == questionId) {
                if (type === "requested") requestedField = data[ele];
                if (type === "remove") deleteField = data[ele];
              }
            });

            temp.push({
              id: item?.id,
              questionId: item?.questionId,
              oldDisplayValue: item?.value,
              newDisplayValue: requestedField || "",
              removeItem: deleteField,
              description: currentCorrectionType?.displayDescription,
            });
          }
        });
      }

      return temp;
    },
    [
      currentCorrectionType,
      showRemoveSubject,
      patientAttribute,
      answers,
      questionJson,
      visitNumber,
    ],
  );

  const onSubmit = useCallback(
    async (data) => {
      setSubmitData(data);
      setShowSubmitPage(true);

      const temp = buildCorrectionItems(data);
      const finalPayload = buildCorrectionPayload(data, temp);

      try {
        setLoad(true);
        const res = await createCorrectionService(
          storage.access_token,
          storage.roleId,
          storage.cultureCode,
          storage.singlePatientAlias,
          selectedSite,
          finalPayload,
        );

        if (res?.data?.message === "Executed successfully!") {
          notifySuccess("Data Correction Added successfully!");
          setShowConfirmation(true);
          setConfirmationData(res?.data?.result);
        }
      } catch (err) {
        if (err?.status === 401) navigate("/unAuthorized");
        notifyError(err?.message || "Something went wrong!");
        console.error("Error submitting correction:", err);
      } finally {
        setLoad(false);
      }
    },
    [buildCorrectionItems, buildCorrectionPayload, storage, siteId, navigate],
  );

  const handleNext = useCallback((data) => {
    setSubmitData(data);
    setShowSubmitPage(true);
  }, []);

  // Navigation handlers
  const handleCancel = useCallback(
    (e) => {
      e.preventDefault();
      handleUnSaveWithFun(
        e,
        isDirty,
        () => navigate("/data-corrections"),
        () => {},
      );
    },
    [handleUnSaveWithFun, isDirty, navigate, id],
  );

  const handleHomeNavigation = useCallback(
    (e) => {
      handleUnsaveNavigation(e, isDirty, "", navigate);
    },
    [handleUnsaveNavigation, isDirty, navigate],
  );

  const handleSubjectNavigation = useCallback(
    (e) => {
      handleUnsaveNavigation(e, isDirty, "update-subject", navigate);
    },
    [handleUnsaveNavigation, isDirty, navigate],
  );

  // Memoized values
  const isNextDisabled = useMemo(
    () => showMergeSubjectWarning || isPatientAttributeDisabled,
    [showMergeSubjectWarning, isPatientAttributeDisabled],
  );

  const breadcrumbs = useMemo(
    () => (
      <p className="bread-crumbs-link-container">
        <button className="bread-crumbs-btn" onClick={handleHomeNavigation}>
          Home
        </button>{" "}
        |
        <button
          className="bread-crumbs-btn"
          onClick={() => navigate("/data-corrections")}
        >
          Data Corrections
        </button>{" "}
        |<button className="bread-crumbs-btn">Create Data Correction</button>-{" "}
        <b style={{ color: "gray" }}>{storage.studyName}</b>
      </p>
    ),
    [
      handleHomeNavigation,
      handleSubjectNavigation,
      navigate,
      subjectName,
      storage.studyName,
    ],
  );

  const formFields = useMemo(
    () => (
      <>
        <div style={{ width: "500px", marginTop: "15px" }}>
          <div>
            <>
              <div
                style={{
                  marginBottom: "10px",
                }}
              >
                <span>Site Name</span>
              </div>
              <SearchableSelect
                control={control}
                data={sitesData}
                name="selectedSiteName"
                optionKey="id"
                optionLabel="name"
              />
              {errors.selectedSiteName && (
                <div className="error-container">
                  {errors.selectedSiteName.message}
                </div>
              )}
            </>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "40px",
                marginTop: "15px",
                marginBottom: "15px",
              }}
            >
              <div>
                <span>Subject </span>
              </div>
            </div>
            <SearchableSelect
              control={control}
              data={subjectsData}
              name="selectedSubjectName"
              optionKey="id"
              optionLabel="patientNumber"
            />
            {errors.selectedSubjectName && (
              <div className="error-container">
                {errors.selectedSubjectName.message}
              </div>
            )}
          </div>
          <div style={{ marginBottom: "15px", marginTop: "15px" }}>
            <span>Type of Correction:</span>
          </div>
          <SearchableSelect
            control={control}
            data={correctionData}
            name="typeOfCorrection"
            optionKey="id"
            optionLabel="displayName"
          />
          {errors.typeOfCorrection && (
            <div className="error-container">
              {errors.typeOfCorrection.message}
            </div>
          )}
        </div>
        <div>
          <div style={{ marginBottom: "10px" }}>
            <span>Reason for Correction:</span>
          </div>
          <TextField
            rows={4}
            type="text"
            {...register("reason")}
            multiline
            sx={{ width: "500px" }}
          />
          {errors.reason && (
            <div className="error-container">{errors.reason.message}</div>
          )}
        </div>
      </>
    ),
    [control, correctionData, errors, register, sitesData, subjectsData],
  );

  const renderCorrectionContent = useMemo(() => {
    if (!typeOfCorrection) return null;

    if (showMergeSubjectWarning) {
      return (
        <p>There are no duplicate instances of subject number {subjectName}</p>
      );
    }

    if (showRemoveSubject) {
      return <p>The Selected Subject will be removed.</p>;
    }

    const isChangeSubjectInfo =
      currentCorrectionType?.displayName ===
      CORRECTION_TYPES.CHANGE_SUBJECT_INFO;

    return (
      <>
        {!isChangeSubjectInfo && (
          <>
            <div>
              <h3 style={{ color: "#4f4f4f" }}>Select a Questionnaire</h3>
            </div>
            <UncontrolledSearchSelect
              data={ddlResponseData}
              name="selectedQuestionnaire"
              optionKey="id"
              value={selectedQuestionnaire}
              onChange={(selectedValue) => {
                setSelectedQuestionnaire(selectedValue);
                const data = ddlResponseData?.find(
                  (ele) => ele?.id === selectedValue,
                );
                setVisitNumber(data?.visitId);
              }}
              optionLabel="visitName"
              extraLabel="diaryEntryDateDisplay"
            />
          </>
        )}
        {(!isChangeSubjectInfo && selectedQuestionnaire) ||
        isChangeSubjectInfo ? (
          <div style={{ marginTop: "10px", width: "100%" }}>
            <DataCorrectionGrid
              control={control}
              register={register}
              disabled={false}
              errors={errors}
              tableData={correctionTableData}
            />
          </div>
        ) : null}
      </>
    );
  }, [
    typeOfCorrection,
    showMergeSubjectWarning,
    showRemoveSubject,
    currentCorrectionType,
    subjectName,
    ddlResponseData,
    selectedQuestionnaire,
    control,
    register,
    errors,
    correctionTableData,
  ]);

  // Conditional rendering
  if (showConfirmation) {
    return (
      <DataCorrectionConfirmation
        correctionData={correctionTableData}
        id={selectedSubject}
        isFromDataCorrection={true}
        data={submitData}
        setShowConfirm={setShowConfirmation}
        subjectName={subjectName}
        isRemovedSubject={showRemoveSubject}
      />
    );
  }

  if (showSubmitPage && submitData) {
    return (
      <SubmitDataCorrection
        description={currentCorrectionType?.displayName}
        siteName={
          sitesData?.find((ele) => ele?.id === selectedSite)?.name || siteName
        }
        subjectName={
          subjectsData?.find((ele) => ele?.id === selectedSubject)
            ?.patientNumber || subjectName
        }
        title="Submit Data Correction"
        id={selectedSubject}
        data={submitData}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        setShowSubmitPage={setShowSubmitPage}
        showPasswordModal={showPasswordModal}
        pageDescription={currentCorrectionType?.displayDescription}
        correctionData={correctionTableData}
        load={load}
        isRemovedSubject={showRemoveSubject}
      />
    );
  }

  if (load) {
    return <Loader />;
  }

  return (
    <div className="content-body">
      {breadcrumbs}

      <Grid2
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%", margin: "0 auto" }}
      >
        <Grid2 item md={12}>
          <p className="page-heading">Create Data Correction</p>
        </Grid2>
      </Grid2>

      <Box
        component="form"
        autoComplete="off"
        sx={{ height: "auto", width: "100%" }}
        onSubmit={handleSubmit(handleNext)}
      >
        <Grid2
          container
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%", margin: "0 auto" }}
          gap={5}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "45px",
            }}
          >
            {/* {correctionDetails} */}
            {formFields}
          </div>
        </Grid2>

        {typeOfCorrection && (
          <Grid2
            container
            alignItems="center"
            justifyContent="flex-start"
            sx={{ width: "55vw", minWidth: "61vw", margin: "20px auto" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "start",
                width: "100%",
              }}
            >
              <h2 style={{ color: "#4f4f4f" }}>
                {currentCorrectionType?.displayName}
              </h2>
            </div>
            {renderCorrectionContent}
          </Grid2>
        )}

        <Grid2
          alignItems="end"
          justifyContent="end"
          container
          gap={3}
          sx={{ marginTop: "2%" }}
        >
          <Grid2 item md={6}>
            <button
              type="button"
              className="create-btn-outlined"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </Grid2>
          <Grid2 item md={6}>
            <button
              type="submit"
              className={isNextDisabled ? "disabled-btn" : "create-btn"}
              disabled={isNextDisabled}
            >
              Next
            </button>
          </Grid2>
        </Grid2>
      </Box>

      {showPasswordModal && (
        <DataCorrectionModal
          password={password}
          setPassword={setPassword}
          setIsAuthorized={setIsAuthorized}
          setShowModal={setShowPasswordModal}
          showModal={showPasswordModal}
          userName={storage.email}
        />
      )}
    </div>
  );
};

export default AddDataCorrections;
